// © 2026 AttainIP Engineering
import {
  rowHomeOrgChoice,
  rowMembership,
  rowType,
  unrecognisedAccountType,
  type UserDirectoryRow,
} from '@/lib/userDirectoryRow';
import { orgChoiceLabel } from '@/lib/orgChoiceLabel';

/**
 * The "Type" cell of the admin Users list (RFC-0510 W41).
 *
 * ONE axis: what KIND of identity this row is. The roles — what the person may
 * DO — are a separate column and are deliberately absent here; an org admin is
 * *also* an enterprise user, and folding the two into one badge would have to
 * lie about one of them (Core's role landscape, 2026-09-02).
 *
 * Three things can appear, and each is a distinct fact:
 *
 * 1. the **kind** — `guest` when the MEMBERSHIP says so (a guest's user row
 *    says `enterprise`; that is the whole reason the join exists), otherwise
 *    the user row's account type;
 * 2. **revoked** — a revoked guest keeps their user row, and "guest, revoked"
 *    is a state an operator needs to see;
 * 3. an **unrecognised account type** — shown as itself, never relabelled as a
 *    kind this build happens to know. It is rendered even when the guest
 *    membership has taken over the badge, so it can never be swallowed.
 */
export function UserRowIdentity({ row }: { row: UserDirectoryRow }) {
  const type = rowType(row);
  const membership = rowMembership(row);
  const stray = unrecognisedAccountType(row);
  const home = rowHomeOrgChoice(row);

  const style = !type.recognised
    ? 'bg-amber-50 text-amber-800 ring-amber-600/30'
    : type.value === 'guest'
    ? 'bg-purple-50 text-purple-700 ring-purple-600/20'
    : type.value === 'enterprise'
    ? 'bg-blue-50 text-blue-700 ring-blue-600/20'
    : 'bg-gray-50 text-gray-600 ring-gray-500/20';

  return (
    <div className="flex flex-col gap-0.5">
      <span className="flex items-center gap-1">
        <span
          className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium ring-1 ring-inset ${style}`}
          title={
            type.source === 'membership'
              ? 'Guest: belongs to another organisation (identity.org_memberships.kind)'
              : type.source === 'none'
              ? 'No account type on this user row'
              : undefined
          }
        >
          {type.label}
        </span>
        {membership && !membership.live && (
          <span
            className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium ring-1 ring-inset bg-red-50 text-red-700 ring-red-600/20"
            title="Membership status in this organisation"
          >
            {membership.status}
          </span>
        )}
        {stray && type.source === 'membership' && (
          <span
            className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium ring-1 ring-inset bg-amber-50 text-amber-800 ring-amber-600/30"
            title="Account type on the user row — not one this build knows"
          >
            unrecognised: {stray}
          </span>
        )}
      </span>
      {home && (
        <span className="text-xs text-gray-400">from {orgChoiceLabel(home)}</span>
      )}
    </div>
  );
}
